<template>
  <Transition name="expand">
    <div v-if="show" class="player-expanded-content" @click.stop>
      <div class="expanded-divider"></div>
      <div class="expanded-container">
        <!-- Botón de cerrar -->
        <button
          class="modal-close aa-display-small"
          @click.stop="closeModal"
          aria-label="Cerrar modal"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>

        <!-- Content -->
        <div class="expanded-body">
          <!-- Imagen del show -->
          <div class="show-image">
            <img
              :src="arte(currentShow?.image || astraPlaceHolder)"
              :alt="currentShow?.name"
              @error="$event.target.src = arte(astraPlaceHolder)"
            />
          </div>

          <!-- Información del show -->
          <div class="show-info">
            <!-- Nombre del show -->
            <h2 v-if="currentShow?.name" class="show-name">
              {{ currentShow.name }}
            </h2>

            <div class="show-time">
              <i class="fa-solid fa-clock"></i>
              {{ formatRange(currentShow?.range) }}
            </div>

            <!-- Descripción -->
            <div v-if="currentShow?.subtitle" class="show-description">
              <p>{{ currentShow.subtitle }}</p>
            </div>

            <!-- Géneros -->
            <div v-if="currentShow?.generos?.length" class="show-genres">
              <h3>Géneros principales</h3>
              <div class="genre-tags">
                <span
                  v-for="(genero, index) in currentShow.generos"
                  :key="index"
                  class="genre-tag"
                >
                  {{ genero }}
                </span>
              </div>
            </div>

            <!-- Subgéneros -->
            <div v-if="currentShow?.subGeneros?.length" class="show-subgenres">
              <h3>Subgéneros</h3>
              <p class="subgenres-text">
                {{ currentShow.subGeneros.join(" • ") }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { formatRange } from "@/utils/timeUtils";
import { PLACEHOLDER as astraPlaceHolder, arte } from "@/services/placeholder";

defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  currentShow: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["close"]);

const closeModal = () => {
  emit("close");
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/astra-vars.scss";

.player-expanded-content {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  width: 100%;
  margin-top: $s08;
  background-color: transparent;
  border: 1px solid var(--border);
  border-radius: 0;
  backdrop-filter: blur(15px);
  z-index: 1000;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--bg-cards);
    z-index: -1;
    border-radius: 0;
  }
}

.expanded-divider {
  display: none;
}

.expanded-container {
  display: flex;
  flex-direction: column;
}

.expanded-body {
  padding: $s16 $s16 $s12;
  max-height: 60vh;
  overflow-y: auto;
}

.show-image {
  width: 100%;
  margin-bottom: $s14;
  border-radius: 0;
  overflow: hidden;
  max-height: 300px;

  img {
    width: 100%;
    height: auto;
    max-height: 300px;
    object-fit: cover;
    display: block;
  }
}

.show-info {
  .show-name {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-color);
    margin: 0 0 $s10 0;
    line-height: 1.3;
  }

  .show-time {
    display: flex;
    align-items: center;
    gap: $s08;
    font-size: 0.9rem;
    color: var(--font-color-secondary);
    margin-bottom: $s12;

    i {
      color: var(--accent-color);
    }
  }

  .show-description {
    margin-bottom: $s14;

    p {
      margin: 0;
      line-height: 1.5;
      color: var(--text-color);
      font-size: 0.9rem;
    }
  }

  .show-genres,
  .show-subgenres {
    margin-bottom: $s14;

    h3 {
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--font-color-secondary);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: $s08;
    }
  }

  .genre-tags {
    display: flex;
    flex-wrap: wrap;
    gap: $s08;
  }

  .genre-tag {
    display: inline-block;
    padding: 4px $s12;
    background: var(--accent-color);
    color: white;
    border-radius: 0;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .subgenres-text {
    margin: 0;
    line-height: 1.5;
    color: var(--text-color);
    font-size: 0.85rem;
  }
}

/* Botón de cerrar */
.modal-close {
  position: absolute;
  top: $s16;
  right: $s16;
  width: 36px;
  height: 36px;
  border: none;
  background-color: var(--bg-cards);
  color: var(--text-color);
  border: 1px solid var(--border);
  border-radius: 0;
  cursor: pointer;
  //display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  z-index: 10;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--border);
    transform: scale(1.1);
  }

  i {
    pointer-events: none;
  }
}

/* Transiciones de expansión para Desktop */
@media (min-width: 769px) {
  .expand-enter-active,
  .expand-leave-active {
    transition: max-height 0.3s ease, opacity 0.25s ease;
    overflow: hidden;
  }

  .expand-enter-from,
  .expand-leave-to {
    max-height: 0;
    opacity: 0;
  }

  .expand-enter-to,
  .expand-leave-from {
    max-height: 600px;
    opacity: 1;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .player-expanded-content {
    position: fixed;
    top: 50%;
    left: 0;
    transform: translate(-60%, 0);
    width: calc(100vw - #{$s16 * 2});
    max-width: 100vw;
    margin-top: 0;
    border-radius: 0;
    z-index: 9999;
  }

  .expanded-container {
    width: 100%;
  }

  .expanded-body {
    padding: $s16 $s12;
    max-height: 70vh;
  }

  /* Sin transiciones para mobile - apertura instantánea */
  .expand-enter-active,
  .expand-leave-active {
    transition: none;
  }

  .expand-enter-from,
  .expand-leave-to {
    opacity: 0;
  }

  .expand-enter-to,
  .expand-leave-from {
    opacity: 1;
  }
}
</style>
